import { useEffect, useRef, useState } from 'react'

const HEARTBEAT_TIMEOUT = 60_000 // 60s without heartbeat = stale
const COMMAND_FAILURE_THRESHOLD = 3

/**
 * Tracks extension health: heartbeat freshness (#19/#20) and
 * command failure streaks (#21).
 */
export function useExtensionHealth() {
  const [lastHeartbeat, setLastHeartbeat] = useState<number | null>(null)
  const [lastSync, setLastSync] = useState<number | null>(null)
  const [isStale, setIsStale] = useState(false)
  const [extensionUnresponsive, setExtensionUnresponsive] = useState(false)
  const [extensionInvalidated, setExtensionInvalidated] = useState(false)
  const commandFailures = useRef(0)

  // Listen for extension context invalidation (extension was reloaded)
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // Only trust messages from our own window + our own origin. Prevents a
      // same-origin iframe or third-party script from flipping the health
      // state by posting a fake FWDSLASH_INVALIDATED (which would wedge the
      // UI into "extension broken" state and block the app).
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'FWDSLASH_INVALIDATED') {
        setExtensionInvalidated(true)
      }
    }
    window.addEventListener('message', handler)
    // Also check sentinel attribute
    const checkSentinel = () => {
      const el = document.getElementById('fwdslash-ext')
      if (el?.dataset.invalidated === 'true') setExtensionInvalidated(true)
    }
    const interval = setInterval(checkSentinel, 5000)
    return () => {
      window.removeEventListener('message', handler)
      clearInterval(interval)
    }
  }, [])

  // Listen for heartbeats from content-data.js
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setLastHeartbeat(detail.timestamp)
      setLastSync(detail.lastSync)
      setIsStale(false)
      setExtensionUnresponsive(false)
      commandFailures.current = 0
    }
    window.addEventListener('fwdslash-heartbeat', handler)
    return () => window.removeEventListener('fwdslash-heartbeat', handler)
  }, [])

  // Check for staleness periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastHeartbeat && Date.now() - lastHeartbeat > HEARTBEAT_TIMEOUT) {
        setIsStale(true)
      }
    }, 10_000)
    return () => clearInterval(interval)
  }, [lastHeartbeat])

  // Track command failures (#21)
  const recordCommandFailure = () => {
    commandFailures.current++
    if (commandFailures.current >= COMMAND_FAILURE_THRESHOLD) {
      setExtensionUnresponsive(true)
    }
  }

  const recordCommandSuccess = () => {
    commandFailures.current = 0
    setExtensionUnresponsive(false)
  }

  return {
    lastSync,
    isStale,
    extensionUnresponsive,
    extensionInvalidated,
    recordCommandFailure,
    recordCommandSuccess,
  }
}
